/**
 * =============================================================================
 * IDS CRM Web - Customer Detail Page
 * =============================================================================
 *
 * PURPOSE:
 * Displays full customer details matching the new mockup design with:
 * - Header with avatar initials, large name, status chips
 * - Six computed stat cards below header
 * - Alert bar for time-sensitive warnings
 * - Tabs for Details / Contacts / Assets / Pipeline / Tickets / Timeline
 * - Detail section cards with icon titles and label/value rows
 *
 * =============================================================================
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom';
import BackToList, { useBackToList } from '../components/BackToList';
import { useAuth, API_URL } from '../context/AuthContext';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';
import DistributorProducts from '../components/DistributorProducts';
import DepotsCard from '../components/DepotsCard';
import ContactModal from '../components/ContactModal';
import SearchSelect from '../components/SearchSelect';
import LogCommercialActivityModal from '../components/LogCommercialActivityModal';
import TimelineEntryEditModal from '../components/TimelineEntryEditModal';
import WorkLogFormViewer, { WorkLogFormList } from '../components/WorkLogFormViewer';
import AuditLogPanel from '../components/AuditLogPanel';
import SiteNearbyAccommodation from '../components/SiteNearbyAccommodation';
import TemplateCheckFormRenderer from '../components/TemplateCheckFormRenderer';
import { DEFAULT_ASSET_CONFIG } from '../utils/assetTypes';
import { CUSTOMER_AUDIT_FIELD_LABELS } from '../utils/auditFieldLabels';
import { isFieldEngineer } from '../utils/permissions';
import { formatYield } from '../utils/milkYield';
import { formatMilkingTimes } from '../utils/milkingTimes';
import { getCountryName } from '../utils/countries';
import { formatDate as formatInstantDate, toLocalDateTimeInput } from '../utils/format';
import { dateKeyOf, daysFromToday, formatDateKey, fromDateKey, toDateKey } from '../utils/dates';
import CoordsCopy from '../components/CoordsCopy';
import CustomerStatusChip from '../components/CustomerStatusChip';
import './CustomerDetail.css';

// A customer and a distributor share these pages, and are opened from the End
// Customers list and from Distributors: "←" goes back to the list the page
// was opened from, as that list was left.
const CUSTOMER_LISTS = { '/customers': 'Customers', '/distributors': 'Distributors' };

const COMMERCIAL_OUTCOMES = [
  { value: 'positive', label: 'Positive' },
  { value: 'neutral', label: 'Neutral' },
  { value: 'negative', label: 'Negative' },
  { value: 'no_response', label: 'No Response' }
];

const toDateTimeLocal = (value) => toLocalDateTimeInput(value || undefined) || toLocalDateTimeInput();

function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  // After a delete: back to the customer list as it was left.
  const backToList = useBackToList('/customers', ['/distributors']);
  const [searchParams] = useSearchParams();
  const { authFetch, user } = useAuth();
  // R2 (DL-10): engineers see no money — deal values, pipeline, quotes.
  // Whole engineer family (not just legacy 'engineering') stays money-blind.
  const canSeeMoney = !isFieldEngineer(user?.role);
  const { toastElement, showToast } = useToast();
  const { confirmElement, requestConfirm } = useConfirm();
  const logoInputRef = useRef(null);
  const moreMenuRef = useRef(null);
  const autoOpenedCompletionRef = useRef(null);
  const requestedTab = searchParams.get('tab');
  const completeActivityId = searchParams.get('complete_activity') || searchParams.get('complete_follow_up');

  const [customer, setCustomer] = useState(null);

  // R5b (DL-28): manual account-on-stop flag — management only; PUT is
  // audited server-side and refuses non-managers.
  const refetchCustomer = async () => {
    const response = await authFetch(`/api/v1/customers/${id}`);
    if (response.ok) {
      const data = await response.json();
      setCustomer(data.data || data);
    }
  };

  const toggleAccountStop = () => {
    const puttingOnStop = !customer.account_on_stop;
    requestConfirm({
      title: puttingOnStop ? 'Put account on stop?' : 'Lift account stop?',
      message: puttingOnStop
        ? `${customer.business_name}'s service/repair & replace policy cover will be WITHHELD — policy work bills until the stop is lifted. Warranty obligations are unaffected.`
        : `${customer.business_name}'s policy cover resumes immediately.`,
      confirmLabel: puttingOnStop ? 'Put on stop' : 'Lift stop',
      danger: puttingOnStop,
      onConfirm: async () => {
        try {
          const response = await authFetch(`/api/v1/customers/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ account_on_stop: puttingOnStop })
          });
          if (!response.ok) {
            const body = await response.json().catch(() => ({}));
            throw new Error(body.message || 'Failed to update the account-stop flag');
          }
          showToast(puttingOnStop ? 'Account put on stop — policy cover withheld' : 'Account stop lifted', 'success');
          await refetchCustomer();
        } catch (err) {
          showToast(err.message, 'error');
        }
      }
    });
  };

  // R5b (DL-18): contract approaching its end — the OWNER decides dormant vs
  // keep-on; the banner mirrors the 3-month notification window.
  const renewalDateStr = customer?.service_contract_renewal_date || customer?.contract_renewal_date;
  const renewalDate = fromDateKey(renewalDateStr);
  const contractEndsSoon = Boolean(
    renewalDate && !Number.isNaN(renewalDate.getTime())
    && customer?.customer_type === 'customer'
    && renewalDate <= new Date(new Date().setMonth(new Date().getMonth() + 3))
  );

  // Dormant is set by hand only (Gareth 2026-09-14). The pair sent is valid on
  // both the old and the new API: a dormant end customer is typed former.
  const markDormant = () => {
    requestConfirm({
      title: 'Mark as dormant?',
      message: `${customer.business_name} moves to the Dormant tab and keeps all history. This is your decision — nothing happens automatically.`,
      confirmLabel: 'Mark as dormant',
      danger: true,
      onConfirm: async () => {
        try {
          const response = await authFetch(`/api/v1/customers/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ customer_type: 'former', status: 'dormant' })
          });
          if (!response.ok) {
            const body = await response.json().catch(() => ({}));
            throw new Error(body.message || 'Failed to mark as dormant');
          }
          showToast('Marked as dormant', 'success');
          await refetchCustomer();
        } catch (err) {
          showToast(err.message, 'error');
        }
      }
    });
  };
  const [contacts, setContacts] = useState([]);
  const [farmSites, setFarmSites] = useState([]);
  const [assets, setAssets] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [quotesLoaded, setQuotesLoaded] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [workHistory, setWorkHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(
    ['details', 'contacts', 'sites', 'assets', 'pipeline', 'tickets', 'timeline', 'products', 'depots'].includes(requestedTab)
      ? requestedTab
      : 'details'
  );
  const [assetConfig, setAssetConfig] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [localClock, setLocalClock] = useState(new Date());
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showLinkContactModal, setShowLinkContactModal] = useState(false);
  // Add/Edit contact modal: {contact} to edit, {defaultPlacementId} to
  // pre-scope a new contact to a site/depot, {} for a plain add.
  const [contactModal, setContactModal] = useState(null);
  // The distributor's OWN depots — placement options for its contacts
  // (distinct from chainDepots, which belong to a customer's distributors).
  const [ownDepots, setOwnDepots] = useState([]);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editingTimelineEntry, setEditingTimelineEntry] = useState(null);
  // The form (filled in on a ticket's or job's work log) open in the viewer.
  const [openFormId, setOpenFormId] = useState(null);
  const [completingTimelineEntry, setCompletingTimelineEntry] = useState(null);
  const [savingCommercialCompletion, setSavingCommercialCompletion] = useState(false);
  const [commercialCompletionError, setCommercialCompletionError] = useState(null);
  const [commercialCompletionForm, setCommercialCompletionForm] = useState({
    completion_at: '',
    outcome: 'neutral',
    outcome_notes: '',
    follow_up_required: false,
    follow_up_date: '',
    next_action: ''
  });

  // Country names come from the shared catalogue (utils/countries.js) — this
  // page used to keep its own 14-country map, which named more countries than
  // the address forms could actually select.
  const countryTimeZones = {
    GB: 'Europe/London',
    IE: 'Europe/Dublin',
    DE: 'Europe/Berlin',
    NL: 'Europe/Amsterdam',
    FR: 'Europe/Paris',
    DK: 'Europe/Copenhagen',
    RO: 'Europe/Bucharest',
    SE: 'Europe/Stockholm',
    NO: 'Europe/Oslo',
    FI: 'Europe/Helsinki',
    US: 'America/New_York',
    CA: 'America/Toronto',
    AU: 'Australia/Sydney',
    NZ: 'Pacific/Auckland'
  };

  useEffect(() => {
    if (['details', 'contacts', 'sites', 'assets', 'pipeline', 'tickets', 'timeline', 'products', 'depots'].includes(requestedTab)) {
      setActiveTab(requestedTab);
    }
  }, [requestedTab]);

  useEffect(() => {
    const fetchAssetConfig = async () => {
      try {
        const res = await authFetch('/api/v1/assets/config');
        if (res.ok) {
          const data = await res.json();
          setAssetConfig(data);
        }
      } catch (err) {
        console.error('Failed to fetch asset config:', err);
      }
    };
    fetchAssetConfig();
  }, [authFetch]);

  useEffect(() => {
    const fetchCustomer = async () => {
      try {
        const [customerRes, contactsRes, sitesRes, assetsRes, opportunitiesRes, ticketsRes, timelineRes, workHistoryRes] = await Promise.all([
          authFetch(`/api/v1/customers/${id}`),
          authFetch(`/api/v1/customers/${id}/contacts`),
          authFetch(`/api/v1/customers/${id}/sites`),
          authFetch(`/api/v1/customers/${id}/assets`),
          authFetch(`/api/v1/customers/${id}/opportunities`),
          authFetch(`/api/v1/customers/${id}/tickets`),
          authFetch(`/api/v1/customers/${id}/timeline`),
          authFetch(`/api/v1/customers/${id}/work-history`)
        ]);

        if (!customerRes.ok) {
          throw new Error('Customer not found');
        }

        const customerData = await customerRes.json();
        const contactsData = contactsRes.ok ? await contactsRes.json() : { data: [] };
        const sitesData = sitesRes.ok ? await sitesRes.json() : { data: [] };
        const assetsData = assetsRes.ok ? await assetsRes.json() : { data: [] };
        const opportunitiesData = opportunitiesRes.ok ? await opportunitiesRes.json() : { data: [] };
        const ticketsData = ticketsRes.ok ? await ticketsRes.json() : { data: [] };
        const timelineData = timelineRes.ok ? await timelineRes.json() : { data: [] };
        const workHistoryData = workHistoryRes.ok ? await workHistoryRes.json() : { data: [] };

        setCustomer(customerData);
        setContacts(contactsData.data || []);
        setFarmSites(sitesData.data || []);
        setAssets(assetsData.data || []);
        setOpportunities(opportunitiesData.data || []);
        setTickets(ticketsData.data || []);
        setTimeline(timelineData.data || []);
        setWorkHistory(workHistoryData.data || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCustomer();
  }, [id, authFetch]);

  useEffect(() => {
    const timer = setInterval(() => setLocalClock(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!customer || customer.customer_type !== 'distributor') return;
    const loadOwnDepots = async () => {
      try {
        const response = await authFetch(`/api/v1/distributors/${customer.customer_id}/depots`);
        if (response.ok) {
          const data = await response.json();
          setOwnDepots(data.data || []);
        }
      } catch (err) {
        console.error('Failed to load depots:', err);
      }
    };
    loadOwnDepots();
  }, [customer, authFetch]);

  // Inline "Serviced by depot" on site cards (2026-08-03): the site-level
  // depot link becomes settable from the customer record, not only from the
  // site edit form. Options = the customer's own distributor chain's depots.
  const [chainDepots, setChainDepots] = useState([]);
  // Matches the server's CUSTOMER_EDIT set (site writes carry the link).
  const canSetDepot = ['commercial', 'manager', 'admin', 'team_leader', 'operations_coordinator'].includes(user?.role);

  useEffect(() => {
    if (!customer) return undefined;
    const distributorIds = [...new Set(
      [customer.master_distributor_id, customer.sub_distributor_id].filter(Boolean)
    )];
    if (distributorIds.length === 0) {
      setChainDepots([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const lists = await Promise.all(distributorIds.map(async (distributorId) => {
          const response = await authFetch(`/api/v1/distributors/${distributorId}/depots`);
          if (!response.ok) return [];
          const body = await response.json();
          return body.data || [];
        }));
        if (!cancelled) setChainDepots(lists.flat());
      } catch {
        // Picker simply stays hidden.
      }
    })();
    return () => { cancelled = true; };
  }, [customer, authFetch]);

  const updateSiteDepot = async (site, depotId) => {
    try {
      const response = await authFetch(`/api/v1/customers/${id}/sites/${site.site_id}`, {
        method: 'PUT',
        body: JSON.stringify({ servicing_depot_id: depotId })
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || 'Failed to update servicing depot');
      }
      const sitesRes = await authFetch(`/api/v1/customers/${id}/sites`);
      if (sitesRes.ok) {
        const body = await sitesRes.json();
        setFarmSites(body.data || []);
      }
      showToast('Servicing depot updated', 'success');
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  const renderSiteDepot = (site) => {
    if (canSetDepot && chainDepots.length > 0) {
      return (
        <div className="site-depot-picker">
          <span>Serviced by</span>
          <select
            value={site.servicing_depot_id || ''}
            onChange={(event) => updateSiteDepot(site, event.target.value)}
          >
            <option value="">Not assigned</option>
            {chainDepots.map(depot => (
              <option key={depot.depot_id} value={depot.depot_id}>
                {depot.depot_name}{depot.is_head_office ? ' (Head office)' : ''}
              </option>
            ))}
          </select>
        </div>
      );
    }
    return site.servicing_depot_name
      ? <p className="site-depot-note">Serviced by {site.servicing_depot_name}</p>
      : null;
  };

  // Quotes are loaded lazily when the Quotes tab is first opened so the main
  // customer fetch (and its tests) are unaffected.
  useEffect(() => {
    if (activeTab !== 'quotes' || quotesLoaded) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`/api/v1/quotes?customer_id=${id}`);
        if (res.ok && !cancelled) setQuotes((await res.json()).data || []);
      } catch (err) { /* non-fatal */ }
      if (!cancelled) setQuotesLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [activeTab, quotesLoaded, authFetch, id]);

  const refreshContacts = async () => {
    try {
      const response = await authFetch(`/api/v1/customers/${id}/contacts`);
      if (response.ok) {
        const data = await response.json();
        setContacts(data.data || []);
      }
    } catch (err) {
      console.error('Failed to refresh contacts:', err);
    }
  };

  const refreshTimeline = async () => {
    try {
      const [timelineRes, workHistoryRes] = await Promise.all([
        authFetch(`/api/v1/customers/${id}/timeline`),
        authFetch(`/api/v1/customers/${id}/work-history`)
      ]);

      const timelineData = timelineRes.ok ? await timelineRes.json() : { data: [] };
      const workHistoryData = workHistoryRes.ok ? await workHistoryRes.json() : { data: [] };
      setTimeline(timelineData.data || []);
      setWorkHistory(workHistoryData.data || []);
    } catch (err) {
      console.error('Failed to refresh customer timeline:', err);
    }
  };

  const handleLogoClick = () => {
    logoInputRef.current?.click();
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showToast('Please select an image file', 'error');
      return;
    }

    // Validate file size (2MB)
    if (file.size > 2 * 1024 * 1024) {
      showToast('Image must be less than 2MB', 'error');
      return;
    }

    setUploadingLogo(true);

    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result;

        const response = await authFetch(`/api/v1/customers/${id}/logo`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ logo: base64 })
        });

        if (response.ok) {
          const data = await response.json();
          setCustomer(prev => ({ ...prev, logo_url: data.logo_url }));
        } else {
          const err = await response.json();
          showToast(err.message || 'Failed to upload logo', 'error');
        }

        setUploadingLogo(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error('Logo upload error:', err);
      showToast('Failed to upload logo', 'error');
      setUploadingLogo(false);
    }

    // Clear input so same file can be selected again
    e.target.value = '';
  };

  const handleRemoveLogo = () => {
    requestConfirm({
      title: 'Remove logo?',
      message: 'Remove this customer\'s logo?',
      confirmLabel: 'Remove',
      danger: true,
      onConfirm: async () => {
        try {
          const response = await authFetch(`/api/v1/customers/${id}/logo`, {
            method: 'DELETE'
          });
          if (response.ok) {
            setCustomer(prev => ({ ...prev, logo_url: null }));
          }
        } catch (err) {
          console.error('Logo remove error:', err);
        }
      }
    });
  };

  const handleDeleteCustomer = async () => {
    setDeleting(true);
    try {
      const response = await authFetch(`/api/v1/customers/${id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        backToList();
      } else {
        const data = await response.json();
        showToast(data.message || 'Failed to delete customer', 'error');
      }
    } catch (err) {
      console.error('Delete customer error:', err);
      showToast('Failed to delete customer', 'error');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  // Close more menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target)) {
        setShowMoreMenu(false);
      }
    };

    if (showMoreMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMoreMenu]);

  const getInitials = (name) => {
    if (!name) return '??';
    const words = name.split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // DATE columns (next_action_date, install and due dates) are calendar days.
  // Ticket and timeline timestamps are instants: formatInstantDate.
  const formatDate = (dateStr) => formatDateKey(dateStr, { day: 'numeric', month: 'short', year: 'numeric' }, '—');

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getWorkTypeLabel = (type) => ({
    reactive_ticket: 'Reactive Ticket',
    scheduled_work: 'Scheduled Work'
  }[type] || type?.replace(/_/g, ' '));

  const displayHistoryValue = (value) => {
    if (value === null || value === undefined || value === '') return 'Not recorded';
    return String(value).replace(/_/g, ' ');
  };

  const getTimelineBodyField = (body, label) => {
    const line = String(body || '').split('\n').find(item => item.toLowerCase().startsWith(`${label.toLowerCase()}:`));
    return line ? line.slice(label.length + 1).trim() : '';
  };

  const isCompletedCommercialFollowUp = (entry) => (
    entry.linked_record_type === 'commercial_follow_up_completed' ||
    getTimelineBodyField(entry.body, 'Completion status').toLowerCase() === 'completed'
  );

  const isScheduledCommercialFollowUp = (entry) => Boolean(entry.next_action_date);

  const isCommercialFollowUp = (entry) => (
    isScheduledCommercialFollowUp(entry) ||
    isCompletedCommercialFollowUp(entry) ||
    entry.linked_record_type === 'commercial_follow_up_parent'
  );

  const getCommercialFollowUpStatus = (entry) => {
    if (isCompletedCommercialFollowUp(entry)) return 'completed';
    if (entry.next_action_date && dateKeyOf(entry.next_action_date) < toDateKey(new Date())) return 'overdue';
    return 'scheduled';
  };

  const getCommercialContactName = (entry) => entry.contact_name || getTimelineBodyField(entry.body, 'Contact');

  const getCommercialMethod = (entry) => getTimelineBodyField(entry.body, 'Communication method');

  const getCommercialTimelineRows = (entry) => {
    const activityType = displayHistoryValue(entry.entry_type);
    const method = getCommercialMethod(entry);
    const contactName = getCommercialContactName(entry);

    if (isCompletedCommercialFollowUp(entry)) {
      return [
        `Activity: ${activityType}`,
        method ? `Method: ${method}` : null,
        getTimelineBodyField(entry.body, 'Completion date/time') ? `Completed: ${formatDateTime(getTimelineBodyField(entry.body, 'Completion date/time'))}` : null,
        getTimelineBodyField(entry.body, 'Completed by') ? `Completed by: ${getTimelineBodyField(entry.body, 'Completed by')}` : entry.user_name ? `Completed by: ${entry.user_name}` : null,
        getTimelineBodyField(entry.body, 'Completion outcome') ? `Outcome: ${displayHistoryValue(getTimelineBodyField(entry.body, 'Completion outcome'))}` : entry.outcome ? `Outcome: ${displayHistoryValue(entry.outcome)}` : null,
        getTimelineBodyField(entry.body, 'Completion notes') ? `Notes: ${getTimelineBodyField(entry.body, 'Completion notes')}` : null,
        getTimelineBodyField(entry.body, 'Next follow-up required') ? `Follow-up: ${getTimelineBodyField(entry.body, 'Next follow-up required')}` : null
      ].filter(Boolean);
    }

    return [
      `Activity: ${activityType}`,
      method ? `Method: ${method}` : null,
      entry.next_action_date ? `Scheduled: ${formatDate(entry.next_action_date)}` : null,
      entry.user_name ? `Assigned: ${entry.user_name}` : null,
      contactName ? `Contact: ${contactName}` : null,
      entry.next_action ? `Purpose: ${entry.next_action}` : entry.subject ? `Purpose: ${entry.subject}` : null,
      `Status: ${displayHistoryValue(getCommercialFollowUpStatus(entry))}`
    ].filter(Boolean);
  };

  const getTimelineSortDate = (entry) => {
    if (isCompletedCommercialFollowUp(entry)) {
      return getTimelineBodyField(entry.body, 'Completion date/time') || entry.updated_at || entry.occurred_at;
    }
    if (isScheduledCommercialFollowUp(entry)) return entry.next_action_date;
    return entry.occurred_at;
  };

  const openCommercialCompletion = (entry) => {
    if (!entry || !isScheduledCommercialFollowUp(entry) || isCompletedCommercialFollowUp(entry)) return;

    setCompletingTimelineEntry(entry);
    setCommercialCompletionError(null);
    setCommercialCompletionForm({
      completion_at: toDateTimeLocal(),
      outcome: entry.outcome || 'neutral',
      outcome_notes: '',
      follow_up_required: false,
      follow_up_date: '',
      next_action: ''
    });
  };

  const closeCommercialCompletion = () => {
    setCompletingTimelineEntry(null);
    setCommercialCompletionError(null);

    if (completeActivityId) {
      navigate(`/customers/${id}?tab=timeline`, { replace: true });
    }
  };

  const updateCommercialCompletionForm = (field, value) => {
    setCommercialCompletionForm(prev => ({
      ...prev,
      [field]: value,
      ...(field === 'follow_up_required' && !value ? { follow_up_date: '', next_action: '' } : {})
    }));
  };

  const completeCommercialFollowUp = async (event) => {
    event.preventDefault();
    if (!completingTimelineEntry) return;

    setSavingCommercialCompletion(true);
    setCommercialCompletionError(null);

    if (!commercialCompletionForm.completion_at) {
      setCommercialCompletionError('Completion date/time is required');
      setSavingCommercialCompletion(false);
      return;
    }

    const completionDate = new Date(commercialCompletionForm.completion_at);
    if (Number.isNaN(completionDate.getTime())) {
      setCommercialCompletionError('Enter a valid completion date/time');
      setSavingCommercialCompletion(false);
      return;
    }

    if (commercialCompletionForm.follow_up_required && !commercialCompletionForm.follow_up_date) {
      setCommercialCompletionError('Follow-up date is required when follow-up is selected');
      setSavingCommercialCompletion(false);
      return;
    }

    if (commercialCompletionForm.follow_up_required && !commercialCompletionForm.next_action.trim()) {
      setCommercialCompletionError('Next activity/purpose is required when follow-up is selected');
      setSavingCommercialCompletion(false);
      return;
    }

    try {
      const response = await authFetch(`/api/v1/timeline/${completingTimelineEntry.entry_id}/complete-follow-up`, {
        method: 'POST',
        body: JSON.stringify({
          completion_at: completionDate.toISOString(),
          outcome: commercialCompletionForm.outcome,
          outcome_notes: commercialCompletionForm.outcome_notes || null,
          follow_up_required: commercialCompletionForm.follow_up_required,
          follow_up_date: commercialCompletionForm.follow_up_required ? commercialCompletionForm.follow_up_date : null,
          next_action: commercialCompletionForm.follow_up_required ? commercialCompletionForm.next_action : null
        })
      });

      if (!response.ok) {
        let errorMessage = 'Failed to complete commercial follow-up';
        try {
          const data = await response.json();
          errorMessage = data.message || errorMessage;
        } catch {
          // Keep generic message if the server did not return JSON.
        }
        throw new Error(errorMessage);
      }

      await refreshTimeline();
      setActiveTab('timeline');
      closeCommercialCompletion();
    } catch (err) {
      setCommercialCompletionError(err.message || 'Failed to complete commercial follow-up');
    } finally {
      setSavingCommercialCompletion(false);
    }
  };

  const getWorkHistoryText = (item) => [
    `Status: ${displayHistoryValue(item.status)}`,
    item.assigned_to_name ? `Assigned: ${item.assigned_to_name}` : null,
    item.site_work_required ? 'Site work required' : null,
    Number(item.asset_count) > 1 && item.asset_refs ? `Assets: ${item.asset_refs}` : item.asset_ref ? `Asset: ${item.asset_ref}` : null,
    item.site_name ? `Site: ${item.site_name}` : null,
    item.summary ? `Summary: ${item.summary}` : null,
    item.completed_date ? `Completed: ${formatDateTime(item.completed_date)}` : null
  ].filter(Boolean).join(' | ');

  const combinedTimeline = [
    ...workHistory.map(item => ({
      ...item,
      entry_id: `work-${item.work_type}-${item.id}`,
      entry_type: 'support_job',
      occurred_at: item.completed_date || item.date_raised,
      subject: `${item.ref} - ${item.title}`,
      body: getWorkHistoryText(item),
      user_name: item.assigned_to_name,
      isWorkHistory: true
    })),
    ...timeline.map(entry => ({ ...entry, isWorkHistory: false }))
  ].sort((a, b) => new Date(getTimelineSortDate(b) || 0) - new Date(getTimelineSortDate(a) || 0));

  useEffect(() => {
    if (!completeActivityId || autoOpenedCompletionRef.current === completeActivityId) return;

    const entry = timeline.find(item => item.entry_id === completeActivityId);
    if (!entry) return;

    setActiveTab('timeline');
    autoOpenedCompletionRef.current = completeActivityId;
    if (!entry.next_action_date || entry.linked_record_type === 'commercial_follow_up_completed') return;

    setCompletingTimelineEntry(entry);
    setCommercialCompletionError(null);
    setCommercialCompletionForm({
      completion_at: toDateTimeLocal(),
      outcome: entry.outcome || 'neutral',
      outcome_notes: '',
      follow_up_required: false,
      follow_up_date: '',
      next_action: ''
    });
  }, [completeActivityId, timeline]);

  const displayValue = (value) => {
    if (value === null || value === undefined || value === '') return 'Not recorded';
    return String(value).replace(/_/g, ' ');
  };

  const displayNumber = (value) => {
    if (value === null || value === undefined || value === '') return 'Not recorded';
    return Number(value).toLocaleString();
  };

  const displayCoordinates = (site) => {
    const lat = site.lat ?? site.latitude;
    const lng = site.lng ?? site.longitude;
    if (lat === null || lat === undefined || lat === '' || lng === null || lng === undefined || lng === '') {
      return 'Not recorded';
    }
    return `${Number(lat).toFixed(7)}, ${Number(lng).toFixed(7)}`;
  };

  const displayCurrency = (minorUnits, currency = 'GBP') => {
    if (minorUnits === null || minorUnits === undefined || minorUnits === '') return 'Not recorded';
    const amount = minorUnits / 100;
    const symbol = currency === 'EUR' ? 'EUR ' : currency === 'USD' ? '$' : 'GBP ';
    if (amount >= 1000) return `${symbol}${(amount / 1000).toFixed(1)}k`;
    return `${symbol}${amount.toFixed(0)}`;
  };

  const getPrimarySite = () => {
    return farmSites.find(site => site.is_primary) || farmSites[0] || null;
  };

  const getOperationalLocation = () => {
    return getPrimarySite() || customer;
  };

  const getLocationLabel = () => {
    const location = getOperationalLocation();
    const parts = [location.town_city, location.county_region, location.country_code].filter(Boolean);
    return parts.length ? parts.join(', ') : 'Location not recorded';
  };

  const getCorporateAddress = () => [
    customer.address_line_1,
    customer.address_line_2,
    customer.town_city,
    customer.county_region,
    customer.state_province,
    customer.postcode
  ].filter(Boolean).join(', ');

  const getSiteAddress = (site) => [
    site.address_line_1,
    site.address_line_2,
    site.town_city,
    site.county_region,
    site.state_province,
    site.postcode
  ].filter(Boolean).join(', ');

  const getSiteTimezoneInfo = (site) => {
    if (site.timezone) {
      return {
        timeZone: site.timezone,
        source: 'From site location'
      };
    }

    const derivedTimeZone = countryTimeZones[site.country_code];
    if (derivedTimeZone) {
      return {
        timeZone: derivedTimeZone,
        source: `Estimated from ${getCountryName(site.country_code)}`
      };
    }

    return { timeZone: null, source: 'Timezone not available' };
  };

  // Customer-level twin of getSiteTimezoneInfo: the server derives
  // customers.timezone from the record's coordinates/country; records it
  // couldn't resolve fall back to a country estimate for display only.
  const getCustomerTimezoneInfo = () => {
    if (customer?.timezone) {
      return { timeZone: customer.timezone, source: 'From record location' };
    }
    const derivedTimeZone = countryTimeZones[customer?.country_code];
    if (derivedTimeZone) {
      return {
        timeZone: derivedTimeZone,
        source: `Estimated from ${getCountryName(customer.country_code)}`
      };
    }
    return { timeZone: null, source: 'Timezone not available' };
  };

  const formatLocalTime = (timeZone) => {
    if (!timeZone) return 'Not available';
    try {
      return new Intl.DateTimeFormat('en-GB', {
        timeZone,
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).format(localClock);
    } catch (err) {
      return 'Invalid timezone';
    }
  };

  const formatTimezoneOffset = (timeZone) => {
    if (!timeZone) return '';
    try {
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone,
        timeZoneName: 'shortOffset'
      }).formatToParts(localClock);
      return parts.find(part => part.type === 'timeZoneName')?.value || '';
    } catch (err) {
      return '';
    }
  };

  const getRelativeTime = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'today';
    if (diffDays === 1) return 'yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  // Find alerts (brush replacement, service due, etc.)
  const getAlerts = () => {
    const alerts = [];
    // Safety net for the create funnel (Gareth 2026-08-21): a farm customer
    // without a site is invisible to assets, jobs, maps and every herd report.
    if (customer && customer.customer_type !== 'distributor' && farmSites.length === 0) {
      alerts.push({
        type: 'warning',
        message: 'No farm site recorded — assets, jobs, maps and herd reporting all need one.',
        action: { label: 'Add Farm Site', link: `/customers/${customer.customer_id}/sites/new` }
      });
    }
    if (assets && assets.length > 0) {
      assets.forEach(asset => {
        if (asset.brush_replacement_due) {
          const daysUntil = daysFromToday(asset.brush_replacement_due);
          if (daysUntil <= 14 && daysUntil > 0) {
            alerts.push({
              type: 'warning',
              message: `Brush replacement due in ${daysUntil} days on ${asset.asset_ref}. Last replaced ${formatDate(asset.brush_install_date)}.`
            });
          }
        }
        const nextMaintenanceDue = asset.next_maintenance_due || asset.next_service_due;
        if (nextMaintenanceDue) {
          const daysUntil = daysFromToday(nextMaintenanceDue);
          if (daysUntil <= 14 && daysUntil > 0) {
            alerts.push({
              type: 'info',
              message: `Maintenance due in ${daysUntil} days on ${asset.asset_ref}.`,
              action: { label: 'View', link: `/assets/${asset.asset_id}` }
            });
          }
        }
      });
    }
    return alerts;
  };

  // Compute stats
  const computeStats = () => {
    const openTickets = tickets.filter(t => !['resolved', 'closed'].includes(t.status));
    const highPriorityTickets = openTickets.filter(t => ['high', 'critical'].includes(t.priority));
    const wonOpportunities = opportunities.filter(o => o.won_at);
    const lifetimeValue = wonOpportunities.reduce((sum, o) => sum + (o.value_minor_units || 0), 0);
    const activeAssets = assets.filter(a => a.status === 'active');
    // Accumulative across all sites (Gareth 2026-08-21): the customer card is
    // a rollup — the per-site truth lives on the site cards below.
    const herdTotal = farmSites.reduce((sum, site) => sum + (Number(site.cows_milking) || 0), 0);
    const totalHerdSize = farmSites.reduce((sum, site) => sum + (Number(site.cows_onsite) || 0), 0);

    // Find next maintenance due
    let nextService = null;
    activeAssets.forEach(asset => {
      const nextDue = asset.next_maintenance_due || asset.next_service_due;
      if (nextDue) {
        if (!nextService || new Date(nextDue) < new Date(nextService.date)) {
          nextService = {
            date: nextDue,
            type: asset.robot_type,
            daysUntil: daysFromToday(nextDue)
          };
        }
      }
    });

    return {
      herd: herdTotal,
      totalHerdSize,
      sites: farmSites.length || Number(customer?.site_count) || 0,
      activeAssets: activeAssets.length,
      openTickets: openTickets.length,
      highPriorityTickets: highPriorityTickets.length,
      lifetimeValue,
      wonOpportunities: wonOpportunities.length,
      contacts: contacts.length,
      nextService
    };
  };

  if (loading) {
    return <div className="loading-page">Loading customer...</div>;
  }

  if (error) {
    return (
      <div className="error-page">
        <h2>Error</h2>
        <p>{error}</p>
        <BackToList to="/customers" also={['/distributors']} labels={CUSTOMER_LISTS} className="btn-secondary">← Customers</BackToList>
      </div>
    );
  }

  const stats = computeStats();
  const alerts = getAlerts();

  return (
    <div className="customer-detail">
      {toastElement}
      {confirmElement}
      {/* Hidden file input for logo upload */}
      <input
        type="file"
        ref={logoInputRef}
        onChange={handleLogoChange}
        accept="image/*"
        style={{ display: 'none' }}
      />

      {/* Header */}
      <div className="detail-header">
        <div className="header-left">
          <div
            className={`customer-avatar ${uploadingLogo ? 'uploading' : ''}`}
            onClick={handleLogoClick}
            title="Click to upload logo"
          >
            {customer.logo_url ? (
              <>
                <img
                  src={`${API_URL}${customer.logo_url}`}
                  alt={customer.business_name}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <span className="avatar-initials" style={{ display: 'none' }}>
                  {getInitials(customer.business_name)}
                </span>
                <button
                  className="remove-logo-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveLogo();
                  }}
                  title="Remove logo"
                >
                  ×
                </button>
              </>
            ) : (
              <>
                <span className="avatar-initials">{getInitials(customer.business_name)}</span>
                <span className="avatar-upload-hint">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                    <polyline points="17 8 12 3 7 8"/>
                    <line x1="12" y1="3" x2="12" y2="15"/>
                  </svg>
                </span>
              </>
            )}
            {uploadingLogo && <span className="upload-spinner"></span>}
          </div>
          <div className="header-info">
            <div className="header-row-1">
              <h1>{customer.business_name}</h1>
              <span className="customer-ref">{customer.customer_number}</span>
              {/* End customers show their status, which their type follows;
                  a distributor keeps its type chip beside its status. */}
              {customer.customer_type === 'distributor' && (
                <span className={`type-chip ${customer.customer_type}`}>{customer.customer_type}</span>
              )}
              <CustomerStatusChip status={customer.status} />
            </div>
            <div className="header-row-2">
              <span
                className={`health-chip ${customer.health_score || 'unknown'}`}
                title={customer.health_score_reason || 'Health not yet computed'}
              >
                <span className="health-dot"></span>
                {customer.health_score || 'Unknown'}
              </span>
              {customer.health_score_reason && (
                <span className="health-reason-text">{customer.health_score_reason}</span>
              )}
              {customer.account_on_stop && (
                <span
                  className="health-chip red"
                  title="Account on stop — service/repair & replace policy cover is withheld; policy work bills until lifted"
                >
                  <span className="health-dot"></span>
                  Account on stop
                </span>
              )}
              {['manager', 'admin'].includes(user?.role) && (
                <button className="btn-link" onClick={toggleAccountStop}>
                  {customer.account_on_stop ? 'Lift account stop' : 'Put account on stop'}
                </button>
              )}
              <span className="territory-text">
                {customer.territory || 'No territory'} | {getCountryName(customer.country_code, 'Not recorded')}
                {getCustomerTimezoneInfo().timeZone && (
                  <> | {formatLocalTime(getCustomerTimezoneInfo().timeZone)} local</>
                )}
              </span>
            </div>
            {contractEndsSoon && (
              <div className="duplicate-ticket-warning" style={{ marginTop: 8 }}>
                <strong>Contract {renewalDate <= new Date() ? 'ended' : 'ending'} {renewalDate.toLocaleDateString('en-GB')}.</strong>{' '}
                Decide: renew / keep them on for future opportunities, or mark them dormant — nothing changes automatically.
                {['manager', 'admin', 'commercial'].includes(user?.role) && (
                  <>
                    {' '}<button className="btn-link btn-link-danger" onClick={markDormant}>Mark as dormant</button>
                    {' '}<Link className="btn-link" to={`/customers/${id}/edit`}>Update renewal date</Link>
                  </>
                )}
              </div>
            )}
            <div className="header-row-3">
              <span className="meta-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="10" r="3"/>
                  <path d="M12 21.7C17.3 17 20 13 20 10a8 8 0 10-16 0c0 3 2.7 7 8 11.7z"/>
                </svg>
                {getLocationLabel()}
              </span>
              {customer.master_distributor_name && (
                <span className="meta-item">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4z"/>
                  </svg>
                  Distributor: <Link to={`/customers/${customer.master_distributor_id}`} className="distributor-link">
                    {customer.master_distributor_name}
                  </Link>
                </span>
              )}
              <span className="meta-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                </svg>
                Account: {customer.account_owner_name || 'Unassigned'}
              </span>
            </div>
            <div className="header-row-4">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
              Record updated <span className="time-link">{getRelativeTime(customer.updated_at) || 'unknown'}</span>
            </div>
          </div>
        </div>
        <div className="header-actions">
          {/* RR5 (+ UX feedback #7): raise a ticket straight from the
              customer record — for commercial this is THE intake path. */}
          <Link to={`/tickets/new?customer_id=${customer.customer_id}`} className="btn-action">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/>
            </svg>
            Raise Service Request
          </Link>
          <button type="button" className="btn-action btn-action-primary" onClick={() => setShowActivityModal(true)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Log Activity
          </button>
          <button type="button" className="btn-action" onClick={() => setShowScheduleModal(true)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            Schedule Activity
          </button>
          {/* One button serves both worlds: distributor records show their
              whole book, farm customers their chain (Gareth 2026-08-21). */}
          <Link
            to={`/hierarchy/${customer.customer_type === 'distributor' ? 'distributor' : 'customer'}/${id}`}
            className="btn-action"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 3v5"/>
              <path d="M5 21v-5a2 2 0 012-2h10a2 2 0 012 2v5"/>
              <circle cx="12" cy="5" r="2"/>
              <circle cx="5" cy="19" r="2"/>
              <circle cx="19" cy="19" r="2"/>
            </svg>
            Hierarchy
          </Link>
          <Link to={`/customers/${id}/edit`} className="btn-action">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
            Edit
          </Link>
          <div className="more-menu-container" ref={moreMenuRef}>
            <button
              className="btn-icon-only"
              onClick={() => setShowMoreMenu(!showMoreMenu)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <circle cx="12" cy="12" r="2"/>
                <circle cx="12" cy="5" r="2"/>
                <circle cx="12" cy="19" r="2"/>
              </svg>
            </button>
            {showMoreMenu && (
              <div className="more-menu-dropdown">
                <button
                  className="more-menu-item danger"
                  onClick={() => {
                    setShowMoreMenu(false);
                    setShowDeleteConfirm(true);
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6"/>
                    <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
                    <line x1="10" y1="11" x2="10" y2="17"/>
                    <line x1="14" y1="11" x2="14" y2="17"/>
                  </svg>
                  Delete Customer
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal-dialog">
            <h3>Delete Customer</h3>
            <p>Are you sure you want to delete <strong>{customer.business_name}</strong>?</p>
            <p className="modal-warning">This will archive the customer record. Associated contacts, sites, assets, tickets, pipeline records, and history are retained for audit and reporting.</p>
            <div className="modal-actions">
              <button
                className="btn-cancel"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="btn-delete"
                onClick={handleDeleteCustomer}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Customer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-label">HERD</div>
          <div className="stat-value">{displayNumber(stats.herd)}</div>
          <div className="stat-sub">
            cows milking across sites
            {stats.totalHerdSize > 0 && stats.totalHerdSize !== stats.herd
              ? ` · ${displayNumber(stats.totalHerdSize)} total herd`
              : ''}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">FARM<br/>SITES</div>
          <div className="stat-value">{stats.sites}</div>
          <div className="stat-sub">operational locations</div>
        </div>
        <div className="stat-card divider">
          <div className="stat-label">OPEN<br/>TICKETS</div>
          <div className={`stat-value ${stats.highPriorityTickets > 0 ? 'warning' : ''}`}>
            {stats.openTickets}
          </div>
          <div className="stat-sub">{stats.highPriorityTickets > 0 ? 'high priority' : 'no high priority'}</div>
        </div>
        {canSeeMoney && (
          <div className="stat-card">
            <div className="stat-label">LIFETIME<br/>VALUE</div>
            <div className="stat-value">{displayCurrency(stats.lifetimeValue, customer.currency)}</div>
            <div className="stat-sub">{stats.wonOpportunities} opportunities<br/>won</div>
          </div>
        )}
        <div className="stat-card">
          <div className="stat-label">CONTACTS</div>
          <div className="stat-value">{stats.contacts}</div>
          <div className="stat-sub">active contacts</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">NEXT<br/>SERVICE</div>
          <div className={`stat-value ${stats.nextService && stats.nextService.daysUntil <= 14 ? 'warning' : ''}`}>
            {stats.nextService ? `${stats.nextService.daysUntil}d` : 'None'}
          </div>
          <div className="stat-sub">
            {stats.nextService ? `${stats.nextService.type?.replace(/_/g, ' ')} due ${formatDateKey(stats.nextService.date, { month: 'short' })}` : 'No scheduled service'}
          </div>
        </div>
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="alerts-section">
          {alerts.map((alert, idx) => (
            <div key={idx} className={`alert-bar ${alert.type}`}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              <span className="alert-message">{alert.message}</span>
              {alert.action && (
                <Link to={alert.action.link} className="alert-action">
                  {alert.action.label} <span>↗</span>
                </Link>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="detail-tabs">
        <button
          className={activeTab === 'details' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('details')}
        >
          Details
        </button>
        <button
          className={activeTab === 'contacts' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('contacts')}
        >
          Contacts ({contacts.length})
        </button>
        <button
          className={activeTab === 'sites' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('sites')}
        >
          Sites ({stats.sites})
        </button>
        <button
          className={activeTab === 'assets' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('assets')}
        >
          Assets ({assets.length})
        </button>
        {canSeeMoney && (
          <button
            className={activeTab === 'pipeline' ? 'tab active' : 'tab'}
            onClick={() => setActiveTab('pipeline')}
          >
            Pipeline ({opportunities.length})
          </button>
        )}
        {canSeeMoney && (
          <button
            className={activeTab === 'quotes' ? 'tab active' : 'tab'}
            onClick={() => setActiveTab('quotes')}
          >
            Quotes
          </button>
        )}
        <button
          className={activeTab === 'tickets' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('tickets')}
        >
          Tickets ({tickets.length})
        </button>
        <button
          className={activeTab === 'timeline' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('timeline')}
        >
          Timeline ({combinedTimeline.length})
        </button>
        {customer.customer_type === 'distributor' && canSeeMoney && (
          <button
            className={activeTab === 'products' ? 'tab active' : 'tab'}
            onClick={() => setActiveTab('products')}
          >
            Products
          </button>
        )}
        {customer.customer_type === 'distributor' && (
          <button
            className={activeTab === 'depots' ? 'tab active' : 'tab'}
            onClick={() => setActiveTab('depots')}
          >
            Depots
          </button>
        )}
        <button
          className={activeTab === 'history' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('history')}
        >
          History
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'details' && (
          <div className="details-grid">
            {/* Business Section */}
            <div className="detail-card">
              <div className="card-header">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4z"/>
                </svg>
                <h3>BUSINESS</h3>
              </div>
              <div className="card-body">
                <div className="info-row">
                  <span className="info-label">Legal name</span>
                  <span className="info-value">{displayValue(customer.legal_name)}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Status</span>
                  <CustomerStatusChip status={customer.status} />
                </div>
                <div className="info-row">
                  <span className="info-label">Tier</span>
                  <span className="info-value">{displayValue(customer.tier)}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Territory</span>
                  <span className="info-value">{displayValue(customer.territory)}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Account owner</span>
                  <span className="info-value">{customer.account_owner_name || 'Unassigned'}</span>
                </div>
              </div>
            </div>

            {/* Location Section */}
            <div className="detail-card">
              <div className="card-header">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="10" r="3"/>
                  <path d="M12 21.7C17.3 17 20 13 20 10a8 8 0 10-16 0c0 3 2.7 7 8 11.7z"/>
                </svg>
                <h3>CORPORATE ADDRESS</h3>
              </div>
              <div className="card-body">
                <div className="info-row">
                  <span className="info-label">Address</span>
                  <span className="info-value">
                    {getCorporateAddress() || 'Not recorded'}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Country</span>
                  <span className="info-value">{getCountryName(customer.country_code, 'Not recorded')}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Local time</span>
                  <span className="info-value local-time-value">
                    {formatLocalTime(getCustomerTimezoneInfo().timeZone)}
                    {formatTimezoneOffset(getCustomerTimezoneInfo().timeZone) && (
                      <span className="value-note">{formatTimezoneOffset(getCustomerTimezoneInfo().timeZone)}</span>
                    )}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Timezone</span>
                  <span className="info-value">
                    {getCustomerTimezoneInfo().timeZone || 'Not recorded'}
                    <span className="value-note">{getCustomerTimezoneInfo().source}</span>
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Sites</span>
                  <span className="info-value">
                    {stats.sites}
                  </span>
                </div>
              </div>
            </div>

            {/* Commercial Section */}
            <div className="detail-card">
              <div className="card-header">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="1" x2="12" y2="23"/>
                  <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
                </svg>
                <h3>COMMERCIAL</h3>
              </div>
              <div className="card-body">
                <div className="info-row">
                  <span className="info-label">Heard about us</span>
                  <span className="info-value">
                    {customer.source === 'other'
                      ? (customer.source_other || 'Other')
                      : displayValue(customer.source)}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Sale channel</span>
                  <span className={`channel-chip ${customer.sale_channel || 'not-recorded'}`}>
                    {customer.sale_channel === 'direct' ? 'Direct' : displayValue(customer.sale_channel)}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Distributor</span>
                  {customer.master_distributor_id ? (
                    <Link to={`/customers/${customer.master_distributor_id}`} className="link-value">
                      {customer.master_distributor_name} <span className="link-arrow">↗</span>
                    </Link>
                  ) : (
                    <span className="info-value muted">No distributor recorded</span>
                  )}
                </div>
                {customer.customer_type !== 'distributor' && (
                  <div className="info-row">
                    <span className="info-label">Hierarchy</span>
                    <Link to={`/customers/${id}/edit#commercial`} className="link-value">
                      Change direct/distributor link <span className="link-arrow">↗</span>
                    </Link>
                  </div>
                )}
                <div className="info-row">
                  <span className="info-label">Currency</span>
                  <span className="info-value">{displayValue(customer.currency)}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Payment terms</span>
                  <span className="info-value">{displayValue(customer.payment_terms)}</span>
                </div>
                {customer.customer_type !== 'distributor' && (
                  <>
                    <div className="info-row">
                      <span className="info-label">Brush supply</span>
                      <span className="info-value">
                        {customer.brush_supply_channel === 'none' ? 'N/A - no brush supply'
                          : customer.brush_supply_channel === 'distributor' ? 'Via distributor'
                            : 'Direct from IDS'}
                      </span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Brush cadence</span>
                      <span className="info-value">{displayValue(customer.brush_purchase_cadence)}</span>
                    </div>
                    {canSeeMoney && (
                      <div className="info-row">
                        <span className="info-label">Brush price</span>
                        <span className="info-value">{displayCurrency(customer.brush_unit_price_minor_units, customer.currency)}</span>
                      </div>
                    )}
                    <div className="info-row">
                      <span className="info-label">Last brush ship</span>
                      <span className="info-value">{formatDate(customer.brush_last_ship_date)}</span>
                    </div>
                  </>
                )}
                {customer.customer_type === 'distributor' && (
                  <>
                    {canSeeMoney && (
                      <div className="info-row">
                        <span className="info-label">Brush purchase price</span>
                        <span className="info-value">{displayCurrency(customer.brush_distributor_price_minor_units, customer.currency)}</span>
                      </div>
                    )}
                    <div className="info-row">
                      <span className="info-label">Ordering agreement</span>
                      <span className="info-value">{displayValue(customer.brush_ordering_agreement)}</span>
                    </div>
                  </>
                )}
                <div className="info-row">
                  <span className="info-label">Service contract</span>
                  <span className={`contract-chip ${customer.service_contract_type || 'not-recorded'}`}>
                    {displayValue(customer.service_contract_type)}
                  </span>
                </div>
              </div>
            </div>

            {/* Notes Section - full width */}
            {customer.notes && (
              <div className="detail-card full-width">
                <div className="card-header">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/>
                  </svg>
                  <h3>NOTES</h3>
                </div>
                <div className="card-body">
                  <p className="notes-text">{customer.notes}</p>
                </div>
              </div>
            )}

            {farmSites.length > 0 && (
              <div className="operational-sites-heading full-width">
                <div>
                  <h2>Operational Sites</h2>
                  <p>{farmSites.length} {farmSites.length === 1 ? 'site' : 'sites'} recorded against this customer.</p>
                </div>
                <Link to={`/customers/${id}/sites/new`} className="btn-primary-small">+ Add Farm Site</Link>
              </div>
            )}

            {farmSites.map(site => {
              const siteAssets = assets.filter(asset => asset.farm_site_id === site.site_id);
              const siteTimezoneInfo = getSiteTimezoneInfo(site);
              const siteTimezoneOffset = formatTimezoneOffset(siteTimezoneInfo.timeZone);

              return (
                <div key={site.site_id} className="operational-site-record full-width">
                  <div className="site-record-header">
                    <div>
                      <div className="site-title-row">
                        <h3>{site.site_name}</h3>
                        {site.is_primary && <span className="badge primary">Primary</span>}
                        <CustomerStatusChip status={site.status || 'active'} />
                      </div>
                      <p>{getSiteAddress(site) || 'Address not recorded'}</p>
                      {renderSiteDepot(site)}
                    </div>
                    <Link to={`/customers/${id}/sites/${site.site_id}/edit`} className="btn-secondary-small">
                      Edit Site
                    </Link>
                  </div>

                  <div className="site-record-grid">
                    <div className="detail-card">
                      <div className="card-header">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="10" r="3"/>
                          <path d="M12 21.7C17.3 17 20 13 20 10a8 8 0 10-16 0c0 3 2.7 7 8 11.7z"/>
                        </svg>
                        <h3>SITE LOCATION</h3>
                      </div>
                      <div className="card-body">
                        <div className="info-row">
                          <span className="info-label">Territory</span>
                          <span className="info-value">{displayValue(site.territory)}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Country</span>
                          <span className="info-value">{getCountryName(site.country_code, 'Not recorded')}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Coordinates</span>
                          <span className="info-value mono-value">{displayCoordinates(site)}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Geocode source</span>
                          <span className="info-value">{displayValue(site.geocode_source || site.geocode_status)}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Local time</span>
                          <span className="info-value local-time-value">
                            {formatLocalTime(siteTimezoneInfo.timeZone)}
                            {siteTimezoneOffset && <span className="value-note">{siteTimezoneOffset}</span>}
                          </span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Timezone</span>
                          <span className="info-value">
                            {siteTimezoneInfo.timeZone || 'Not recorded'}
                            <span className="value-note">{siteTimezoneInfo.source}</span>
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="detail-card">
                      <div className="card-header">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"/>
                          <path d="M12 2v4M12 18v4M2 12h4M18 12h4"/>
                        </svg>
                        <h3>PARLOUR</h3>
                      </div>
                      <div className="card-body">
                        <div className="info-row">
                          <span className="info-label">Type</span>
                          <span className={`type-chip-small ${site.parlour_type || ''}`}>{displayValue(site.parlour_type)}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Manufacturer</span>
                          <span className="info-value">
                            {site.parlour_manufacturer === 'other'
                              ? displayValue(site.parlour_manufacturer_other)
                              : displayValue(site.parlour_manufacturer)}
                          </span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Size</span>
                          <span className="info-value">{site.parlour_size ? `${site.parlour_size} stalls` : 'Not recorded'}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Speed</span>
                          <span className="info-value">{site.parlour_speed_secs_per_stall ? `${site.parlour_speed_secs_per_stall} secs/stall` : 'Not recorded'}</span>
                        </div>
                      </div>
                    </div>

                    <div className="detail-card">
                      <div className="card-header">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"/>
                          <path d="M12 6v6l4 2"/>
                        </svg>
                        <h3>MILKING SCHEDULE</h3>
                      </div>
                      <div className="card-body">
                        <div className="info-row">
                          <span className="info-label">Milkings/day</span>
                          <span className="info-value">{displayNumber(site.milkings_per_day)}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Milking times</span>
                          <span className="info-value">
                            {formatMilkingTimes(site.milking_start_times, site.milking_end_times) || 'Not recorded'}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="detail-card">
                      <div className="card-header">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/>
                        </svg>
                        <h3>HERD & ASSETS</h3>
                      </div>
                      <div className="card-body">
                        <div className="info-row">
                          <span className="info-label">Total herd size</span>
                          <span className="info-value">{displayNumber(site.cows_onsite)}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Cows milking</span>
                          <span className="info-value highlight">{displayNumber(site.cows_milking)}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Breed</span>
                          <span className="info-value">{displayValue(site.breed)}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Average yield</span>
                          <span className="info-value">{formatYield(site.average_yield_litres, site.country_code) || 'Not recorded'}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Milk buyer</span>
                          <span className="info-value">{displayValue(site.milk_buyer)}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Bedding type</span>
                          <span className="info-value">{displayValue(site.bedding_type)}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Chemical type</span>
                          <span className="info-value">{displayValue(site.chemical_type)}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Assets</span>
                          <Link to={`/customers/${id}?tab=assets`} className="link-value">
                            {siteAssets.length} {siteAssets.length === 1 ? 'asset' : 'assets'}
                          </Link>
                        </div>
                      </div>
                    </div>

                    <div className="detail-card">
                      <div className="card-header">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M2 4v16"/>
                          <path d="M2 8h18a2 2 0 012 2v10"/>
                          <path d="M2 17h20"/>
                          <path d="M6 8v9"/>
                        </svg>
                        <h3>NEARBY ACCOMMODATION</h3>
                      </div>
                      <div className="card-body">
                        <SiteNearbyAccommodation siteId={site.site_id} />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {activeTab === 'contacts' && (
          <div className="contacts-tab">
            <div className="tab-header">
              <h3>Contacts</h3>
              <div className="tab-header-actions">
                <button className="btn-secondary-small" onClick={() => setShowLinkContactModal(true)}>
                  Link Existing Contact
                </button>
                <button className="btn-primary-small" onClick={() => setContactModal({})}>
                  + Add Contact
                </button>
              </div>
            </div>
            {contacts.length === 0 ? (
              <div className="empty-state">
                <p>No contacts added yet</p>
              </div>
            ) : (
              <div className="contacts-grid">
                {contacts.map((contact) => (
                  <div key={contact.contact_id} className="contact-card">
                    <div className="contact-header">
                      <h4>{contact.full_name}</h4>
                      <div className="contact-badges">
                        {contact.is_primary && !contact.via_link && <span className="badge primary">Primary</span>}
                        {contact.is_decision_maker && <span className="badge">Decision Maker</span>}
                        {contact.via_link && (
                          <span className="badge linked" title={`Home organisation: ${contact.other_organisations?.[0]?.business_name || 'another organisation'}`}>
                            Linked{contact.link_role ? `: ${contact.link_role}` : ''}
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="contact-role">
                      {contact.job_title || contact.role || '—'}
                      {(contact.site_name || contact.depot_name) && (
                        <span className="contact-placement"> · {contact.site_name || contact.depot_name}</span>
                      )}
                    </p>
                    {contact.other_organisations?.length > 0 && (
                      <p className="contact-other-orgs">
                        Also at: {contact.other_organisations.map(org => org.business_name).join(', ')}
                      </p>
                    )}
                    {contact.via_link ? (
                      <button
                        className="contact-unlink-btn"
                        onClick={async () => {
                          const response = await authFetch(`/api/v1/contacts/${contact.contact_id}/links/${id}`, { method: 'DELETE' });
                          if (response.ok) refreshContacts();
                        }}
                      >
                        Unlink from this organisation
                      </button>
                    ) : (
                      <button className="btn-link contact-edit-btn" onClick={() => setContactModal({ contact })}>
                        Edit
                      </button>
                    )}
                    <div className="contact-details">
                      {contact.phone && (
                        <a href={`tel:${contact.phone}`} className="contact-link">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/>
                          </svg>
                          {contact.phone}
                        </a>
                      )}
                      {contact.email && (
                        <a href={`mailto:${contact.email}`} className="contact-link">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                            <polyline points="22,6 12,13 2,6"/>
                          </svg>
                          {contact.email}
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'sites' && (
          <div className="sites-tab">
            <div className="tab-header">
              <h3>Farm Sites</h3>
              <Link to={`/customers/${id}/sites/new`} className="btn-primary-small">+ Add Farm Site</Link>
            </div>
            {farmSites.length === 0 ? (
              <div className="empty-state">
                <p>No farm sites added yet</p>
              </div>
            ) : (
              <div className="sites-grid">
                {farmSites.map((site) => {
                  const siteAssets = assets.filter(asset => asset.farm_site_id === site.site_id);
                  const siteAddress = [
                    site.address_line_1,
                    site.address_line_2,
                    site.town_city,
                    site.county_region,
                    site.state_province,
                    site.postcode
                  ].filter(Boolean).join(', ');

                  return (
                    <div key={site.site_id} className="site-card">
                      <div className="site-card-header">
                        <div>
                          <div className="site-title-row">
                            <h4>{site.site_name}</h4>
                            {site.is_primary && <span className="badge primary">Primary</span>}
                          </div>
                          <p>{siteAddress || 'Address not recorded'}</p>
                          {renderSiteDepot(site)}
                        </div>
                        <Link to={`/customers/${id}/sites/${site.site_id}/edit`} className="btn-secondary-small">
                          Edit
                        </Link>
                      </div>
                      <div className="site-metrics">
                        <div>
                          <span className="metric-value">{displayNumber(site.cows_milking)}</span>
                          <span className="metric-label">cows milking</span>
                        </div>
                        <div>
                          <span className="metric-value">{siteAssets.length}</span>
                          <span className="metric-label">assets</span>
                        </div>
                        <div>
                          <span className="metric-value">{site.parlour_type ? site.parlour_type.replace(/_/g, ' ') : 'Not recorded'}</span>
                          <span className="metric-label">parlour</span>
                        </div>
                      </div>
                      <div className="site-details">
                        <span>{site.territory || 'Area not recorded'}</span>
                        <span>{site.timezone || countryTimeZones[site.country_code] || 'Timezone not recorded'}</span>
                        <span className="mono-value">{displayCoordinates(site)}</span>
                        <CoordsCopy lat={site.lat ?? site.latitude} lng={site.lng ?? site.longitude} compact />
                      </div>
                      <div className="site-asset-list">
                        {siteAssets.length === 0 ? (
                          <span className="muted-text">No assets assigned to this site</span>
                        ) : (
                          siteAssets.map(asset => (
                            <Link key={asset.asset_id} to={`/assets/${asset.asset_id}`} className="site-asset-link">
                              {asset.asset_ref} · {asset.robot_type?.replace(/_/g, ' ')}
                            </Link>
                          ))
                        )}
                      </div>
                      <div className="site-contact-list">
                        {contacts.filter(c => c.farm_site_id === site.site_id).map(c => (
                          <span key={c.contact_id} className="site-contact-row">
                            {c.full_name}{c.job_title ? ` (${c.job_title})` : ''}
                            {c.phone && <a href={`tel:${c.phone}`} className="site-contact-phone"> {c.phone}</a>}
                          </span>
                        ))}
                      </div>
                      <div className="site-card-actions">
                        <Link to={`/assets/new?customer_id=${id}&farm_site_id=${site.site_id}`} className="site-add-asset">
                          Add asset to this site
                        </Link>
                        <button
                          className="btn-link site-add-contact"
                          onClick={() => setContactModal({ defaultPlacementId: site.site_id })}
                        >
                          Add contact here
                        </button>
                        <Link to={`/hierarchy/site/${site.site_id}`} className="btn-link">
                          View hierarchy
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'assets' && (
          <div className="assets-tab">
            <div className="tab-header">
              <h3>Installed Assets</h3>
              <Link to={`/assets/new?customer_id=${id}`} className="btn-primary-small">+ Add Asset</Link>
            </div>
            {assets.length === 0 ? (
              <div className="empty-state">
                <p>No assets installed yet</p>
              </div>
            ) : (
              <div className="assets-table">
                <table>
                  <thead>
                    <tr>
                      <th>Asset</th>
                      <th>Farm Site</th>
                      <th>Type</th>
                      <th>Serial</th>
                      <th>Installed</th>
                      <th>Status</th>
                      <th>Next Service</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assets.map((asset) => (
                      <tr key={asset.asset_id}>
                        <td>
                          <Link to={`/assets/${asset.asset_id}`} className="asset-link">
                            {asset.asset_ref}
                          </Link>
                        </td>
                        <td>{asset.site_name || 'Unassigned'}</td>
                        <td className="capitalize">{asset.robot_type?.replace(/_/g, ' ')}</td>
                        <td className="mono">{asset.serial_number}</td>
                        <td>{formatDate(asset.install_date)}</td>
                        <td>
                          <span className={`status-badge ${asset.status}`}>{asset.status}</span>
                        </td>
                        <td>{formatDate(asset.next_maintenance_due || asset.next_service_due)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'pipeline' && (
          <div className="pipeline-tab">
            <div className="tab-header">
              <h3>Opportunities</h3>
              <Link to={`/pipeline/new?customer_id=${id}`} className="btn-primary-small">+ New Opportunity</Link>
            </div>
            {opportunities.length === 0 ? (
              <div className="empty-state">
                <p>No opportunities yet</p>
              </div>
            ) : (
              <div className="opportunities-list">
                {opportunities.map((opp) => (
                  <Link to={`/pipeline/${opp.opportunity_id}`} key={opp.opportunity_id} className="opportunity-card">
                    <div className="opp-header">
                      <span className="opp-ref">{opp.opp_ref}</span>
                      <span className={`opp-stage ${opp.won_at ? 'won' : opp.lost_at ? 'lost' : ''}`}>
                        {opp.lost_at ? 'Lost' : opp.stage}
                      </span>
                    </div>
                    <h4 className="opp-title">{opp.title}</h4>
                    <div className="opp-meta">
                      <span className="opp-value">{displayCurrency(opp.value_minor_units, opp.currency)}</span>
                      <span className="opp-product">{opp.product?.replace(/_/g, ' ')}</span>
                      {opp.opportunity_group_id && (
                        <span
                          className="opp-group-link"
                          role="link"
                          tabIndex={0}
                          onClick={(e) => { e.preventDefault(); window.location.href = `/pipeline/groups/${opp.opportunity_group_id}`; }}
                          onKeyDown={(e) => { if (e.key === 'Enter') window.location.href = `/pipeline/groups/${opp.opportunity_group_id}`; }}
                        >
                          View opportunity →
                        </span>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'quotes' && (
          <div className="quotes-tab">
            <div className="tab-header">
              <h3>Quotes</h3>
              <Link to={`/quotes/new?customer_id=${id}`} className="btn-primary-small">+ New Quote</Link>
            </div>
            {!quotesLoaded ? (
              <div className="empty-state"><p>Loading quotes…</p></div>
            ) : quotes.length === 0 ? (
              <div className="empty-state"><p>No quotes yet</p></div>
            ) : (
              <div className="quotes-table">
                <table>
                  <thead>
                    <tr><th>Quote</th><th>Status</th><th>Lines</th><th>Subtotal</th><th>Created</th></tr>
                  </thead>
                  <tbody>
                    {quotes.map((q) => (
                      <tr key={q.quote_id} className="clickable-row" onClick={() => navigate(`/quotes/${q.quote_id}`)}>
                        <td>{q.quote_ref}</td>
                        <td><span className={`quote-status status-${q.status}`}>{q.status.replace(/_/g, ' ')}</span></td>
                        <td>{q.item_count}</td>
                        <td>{displayCurrency(q.subtotal_minor_units, q.currency)}</td>
                        <td>{new Date(q.created_at).toLocaleDateString('en-GB')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'tickets' && (
          <div className="tickets-tab">
            <div className="tab-header">
              <h3>Support Tickets</h3>
              <Link to={`/tickets/new?customer_id=${id}`} className="btn-primary-small">+ New Ticket</Link>
            </div>
            {tickets.length === 0 ? (
              <div className="empty-state">
                <p>No support tickets yet</p>
              </div>
            ) : (
              <div className="tickets-table">
                <table>
                  <thead>
                    <tr>
                      <th>Ticket</th>
                      <th>Subject</th>
                      <th>Priority</th>
                      <th>Status</th>
                      <th>Opened</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tickets.map((ticket) => (
                      <tr key={ticket.ticket_id}>
                        <td>
                          <Link to={`/tickets/${ticket.ticket_id}`} className="ticket-link">
                            {ticket.ticket_ref}
                          </Link>
                        </td>
                        <td>
                          {ticket.subject}
                          {ticket.previous_ticket_ref && (
                            <span className="followon-history-tag">Follow-on of {ticket.previous_ticket_ref}</span>
                          )}
                        </td>
                        <td>
                          <span className={`priority-badge ${ticket.priority}`}>{ticket.priority}</span>
                        </td>
                        <td>
                          <span className={`status-badge ${ticket.status}`}>{ticket.status?.replace(/_/g, ' ')}</span>
                        </td>
                        <td>{formatInstantDate(ticket.opened_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'timeline' && (
          <div className="timeline-tab">
            <div className="tab-header">
              <h3>Activity Timeline</h3>
            </div>
            {combinedTimeline.length === 0 ? (
              <div className="empty-state">
                <p>No activity recorded yet</p>
              </div>
            ) : (
              <div className="timeline-list">
                {combinedTimeline.map((entry) => {
                  const canCompleteCommercialFollowUp = !entry.isWorkHistory && isScheduledCommercialFollowUp(entry) && !isCompletedCommercialFollowUp(entry);

                  return (
                  <div
                    key={entry.entry_id}
                    className={`timeline-item ${isCommercialFollowUp(entry) ? `commercial-follow-up ${getCommercialFollowUpStatus(entry)}` : ''} ${canCompleteCommercialFollowUp ? 'actionable' : ''}`}
                    role={canCompleteCommercialFollowUp ? 'button' : undefined}
                    tabIndex={canCompleteCommercialFollowUp ? 0 : undefined}
                    onClick={canCompleteCommercialFollowUp ? () => openCommercialCompletion(entry) : undefined}
                    onKeyDown={canCompleteCommercialFollowUp ? (event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        openCommercialCompletion(entry);
                      }
                    } : undefined}
                  >
                    <div className={`timeline-icon ${entry.entry_type}`}>
                      {entry.entry_type === 'call' && '📞'}
                      {entry.entry_type === 'email' && '✉️'}
                      {entry.entry_type === 'meeting' && '👥'}
                      {entry.entry_type === 'site_visit' && '🔧'}
                      {entry.entry_type === 'note' && '📝'}
                      {entry.entry_type === 'demo' && '🎯'}
                      {entry.entry_type === 'quote_sent' && '📄'}
                      {entry.entry_type === 'deal_won' && '🎉'}
                      {entry.entry_type === 'deal_lost' && '❌'}
                      {entry.entry_type === 'support_job' && '🛠️'}
                      {!['call', 'email', 'meeting', 'site_visit', 'note', 'demo', 'quote_sent', 'deal_won', 'deal_lost', 'support_job'].includes(entry.entry_type) && '📋'}
                    </div>
                    <div className="timeline-content">
                      <div className="timeline-header">
                        <span className={`timeline-type ${entry.entry_type}`}>
                          {isCommercialFollowUp(entry) ? 'Commercial follow-up' : entry.entry_type?.replace(/_/g, ' ')}
                        </span>
                        {isCommercialFollowUp(entry) && (
                          <span className={`timeline-status-chip ${getCommercialFollowUpStatus(entry)}`}>
                            {displayHistoryValue(getCommercialFollowUpStatus(entry))}
                          </span>
                        )}
                        <div className="timeline-actions">
                          {canCompleteCommercialFollowUp && (
                            <button
                              type="button"
                              className="timeline-complete-btn"
                              onClick={(event) => {
                                event.stopPropagation();
                                openCommercialCompletion(entry);
                              }}
                            >
                              Complete Activity
                            </button>
                          )}
                          {!entry.isWorkHistory && (
                            <button
                              type="button"
                              className="timeline-edit-btn"
                              onClick={(event) => {
                                event.stopPropagation();
                                setEditingTimelineEntry(entry);
                              }}
                            >
                              Edit
                            </button>
                          )}
                          <span className="timeline-date">
                            {isScheduledCommercialFollowUp(entry) ? formatDate(entry.next_action_date) : formatInstantDate(entry.occurred_at)}
                          </span>
                        </div>
                      </div>
                      <h4>
                        {entry.isWorkHistory && entry.link_path ? (
                          <Link to={entry.link_path}>{entry.subject}</Link>
                        ) : entry.subject}
                      </h4>
                      {entry.isWorkHistory && (
                        <div className="timeline-summary-row">
                          <span>{getWorkTypeLabel(entry.work_type)}</span>
                          <span>{entry.ref}</span>
                          <span>{displayHistoryValue(entry.status)}</span>
                        </div>
                      )}
                      {isCommercialFollowUp(entry) ? (
                        <div className="timeline-summary-list">
                          {getCommercialTimelineRows(entry).map(row => (
                            <span key={row}>{row}</span>
                          ))}
                        </div>
                      ) : entry.body && <p>{entry.body}</p>}
                      {/* The robot and site forms filled in on this ticket's
                          or job's work logs (Gareth 2026-10-12): each opens
                          read only. Van checks are not listed here. */}
                      {entry.isWorkHistory && (
                        <WorkLogFormList forms={entry.forms} onOpen={form => setOpenFormId(form.check_id)} />
                      )}
                      {/* Activity-form answers snapshot (Gareth 2026-08-04):
                          the completed form is the traceability record —
                          rendered read-only from the stored schema so later
                          template edits never change what was answered. */}
                      {entry.form_template_id && entry.form_responses?.schema && (
                        <details className="entry-sales-form" onClick={(event) => event.stopPropagation()}>
                          <summary>
                            Form: {entry.form_responses.template_name || 'Attached form'}
                          </summary>
                          <div className="entry-sales-form-body">
                            <TemplateCheckFormRenderer
                              schema={entry.form_responses.schema}
                              answers={entry.form_responses.answers || {}}
                              stepAttachments={(entry.form_responses.attachments || []).reduce((byStep, item) => {
                                if (item && item.step_key) byStep[item.step_key] = [...(byStep[item.step_key] || []), item];
                                return byStep;
                              }, {})}
                              onAnswersChange={() => {}}
                              disabled
                            />
                          </div>
                        </details>
                      )}
                      {entry.user_name && <span className="timeline-user">by {entry.user_name}</span>}
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'products' && customer.customer_type === 'distributor' && canSeeMoney && (
          <DistributorProducts
            distributorId={customer.customer_id}
            currency={customer.currency || 'GBP'}
            assetTypes={(assetConfig?.asset_types || DEFAULT_ASSET_CONFIG.asset_types).filter(t => t.active !== false)}
          />
        )}

        {activeTab === 'depots' && customer.customer_type === 'distributor' && (
          <DepotsCard
            distributorId={customer.customer_id}
            contacts={contacts}
            onAddContact={(depotId) => setContactModal({ defaultPlacementId: depotId })}
          />
        )}

        {activeTab === 'history' && (
          <AuditLogPanel
            fetchUrl={`/api/v1/customers/${id}/audit-log`}
            fieldLabels={CUSTOMER_AUDIT_FIELD_LABELS}
            emptyMessage="No changes have been recorded for this customer."
          />
        )}
      </div>

      {openFormId && (
        <WorkLogFormViewer checkId={openFormId} authFetch={authFetch} onClose={() => setOpenFormId(null)} />
      )}

      {editingTimelineEntry && (
        <TimelineEntryEditModal
          entry={editingTimelineEntry}
          authFetch={authFetch}
          onClose={() => setEditingTimelineEntry(null)}
          onSaved={(updated) => {
            setTimeline(prev => prev.map(entry => (
              entry.entry_id === updated.entry_id
                ? { ...entry, ...updated, user_name: entry.user_name, contact_name: entry.contact_name }
                : entry
            )));
            setEditingTimelineEntry(null);
          }}
        />
      )}

      {completingTimelineEntry && (
        <div className="modal-overlay">
          <div className="modal-content commercial-completion-modal" onClick={event => event.stopPropagation()}>
            <div className="modal-header">
              <h2>Complete Commercial Activity</h2>
              <button type="button" className="modal-close" onClick={closeCommercialCompletion}>&times;</button>
            </div>

            <form onSubmit={completeCommercialFollowUp}>
              {commercialCompletionError && <div className="modal-error">{commercialCompletionError}</div>}

              <div className="modal-body">
                <div className="commercial-completion-context">
                  <div>
                    <span className="context-label">Customer</span>
                    <strong>{customer.business_name}</strong>
                  </div>
                  <div>
                    <span className="context-label">Scheduled activity</span>
                    <strong>{completingTimelineEntry.next_action || completingTimelineEntry.subject}</strong>
                  </div>
                  {getCommercialContactName(completingTimelineEntry) && (
                    <div>
                      <span className="context-label">Contact</span>
                      <strong>{getCommercialContactName(completingTimelineEntry)}</strong>
                    </div>
                  )}
                  <div>
                    <span className="context-label">Scheduled for</span>
                    <strong>{formatDate(completingTimelineEntry.next_action_date)}</strong>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="commercial_completion_at">Completion date/time *</label>
                    <input
                      type="datetime-local"
                      id="commercial_completion_at"
                      value={commercialCompletionForm.completion_at}
                      onChange={(event) => updateCommercialCompletionForm('completion_at', event.target.value)}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="commercial_completion_outcome">Outcome *</label>
                    <select
                      id="commercial_completion_outcome"
                      value={commercialCompletionForm.outcome}
                      onChange={(event) => updateCommercialCompletionForm('outcome', event.target.value)}
                      required
                    >
                      {COMMERCIAL_OUTCOMES.map(outcome => (
                        <option key={outcome.value} value={outcome.value}>{outcome.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="commercial_completion_notes">Closing notes</label>
                  <textarea
                    id="commercial_completion_notes"
                    value={commercialCompletionForm.outcome_notes}
                    onChange={(event) => updateCommercialCompletionForm('outcome_notes', event.target.value)}
                    rows={4}
                    placeholder="Short summary of what happened"
                  />
                </div>

                <label className="checkbox-label commercial-next-checkbox">
                  <input
                    type="checkbox"
                    checked={commercialCompletionForm.follow_up_required}
                    onChange={(event) => updateCommercialCompletionForm('follow_up_required', event.target.checked)}
                  />
                  Follow-up required
                </label>

                {commercialCompletionForm.follow_up_required && (
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="commercial_next_follow_up_date">Follow-up date *</label>
                      <input
                        type="date"
                        id="commercial_next_follow_up_date"
                        value={commercialCompletionForm.follow_up_date}
                        onChange={(event) => updateCommercialCompletionForm('follow_up_date', event.target.value)}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="commercial_next_action">Next activity/purpose *</label>
                      <input
                        id="commercial_next_action"
                        value={commercialCompletionForm.next_action}
                        onChange={(event) => updateCommercialCompletionForm('next_action', event.target.value)}
                        placeholder="e.g. Confirm order timing"
                        required
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={closeCommercialCompletion}>Cancel</button>
                <button type="submit" className="btn-save" disabled={savingCommercialCompletion}>
                  {savingCommercialCompletion ? 'Saving...' : 'Mark Complete'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showActivityModal && (
        <LogCommercialActivityModal
          authFetch={authFetch}
          customerId={id}
          customerName={customer.business_name}
          initialContacts={contacts}
          onClose={() => setShowActivityModal(false)}
          onSaved={() => {
            setShowActivityModal(false);
            setActiveTab('timeline');
            refreshTimeline();
          }}
        />
      )}

      {showScheduleModal && (
        <LogCommercialActivityModal
          authFetch={authFetch}
          mode="schedule"
          customerId={id}
          customerName={customer.business_name}
          initialContacts={contacts}
          onClose={() => setShowScheduleModal(false)}
          onSaved={() => {
            setShowScheduleModal(false);
            setActiveTab('timeline');
            refreshTimeline();
          }}
        />
      )}

      {contactModal && (
        <ContactModal
          authFetch={authFetch}
          customerId={id}
          isDistributor={customer.customer_type === 'distributor'}
          sites={farmSites}
          depots={ownDepots}
          contact={contactModal.contact || null}
          defaultPlacementId={contactModal.defaultPlacementId || ''}
          onClose={() => setContactModal(null)}
          onSaved={() => {
            setContactModal(null);
            refreshContacts();
          }}
        />
      )}
      {showLinkContactModal && (
        <LinkContactModal
          authFetch={authFetch}
          customerId={id}
          customerName={customer.business_name}
          onClose={() => setShowLinkContactModal(false)}
          onLinked={() => {
            setShowLinkContactModal(false);
            refreshContacts();
          }}
        />
      )}
    </div>
  );
}

/**
 * L12: link an existing contact (from any organisation) to this customer
 * with an optional per-link role - one person, many organisations.
 */
function LinkContactModal({ authFetch, customerId, customerName, onClose, onLinked }) {
  const [linkRole, setLinkRole] = useState('');
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchContacts = useCallback(async (term) => {
    const response = await authFetch(`/api/v1/contacts?search=${encodeURIComponent(term.trim())}&limit=10`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Contact search failed');
    // A contact already belonging to this customer can't be linked again.
    return (data.data || []).filter(c => c.customer_id !== customerId);
  }, [authFetch, customerId]);

  const submitLink = async () => {
    if (!selected) return;
    setSaving(true);
    setError(null);
    try {
      const response = await authFetch(`/api/v1/contacts/${selected.contact_id}/links`, {
        method: 'POST',
        body: JSON.stringify({ customer_id: customerId, link_role: linkRole || null })
      });
      if (response.ok) {
        onLinked();
      } else {
        const data = await response.json().catch(() => ({}));
        setError(data.message || 'Failed to link contact');
      }
    } catch (err) {
      setError('Failed to link contact');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Link Existing Contact</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          {error && <div className="modal-error">{error}</div>}
          <div className="form-group">
            <label>Search contacts (any organisation)</label>
            <SearchSelect
              id="link_contact_search"
              value={selected ? selected.contact_id : ''}
              placeholder="Name, email or phone..."
              autoFocus
              fetchOptions={fetchContacts}
              getOptionId={(contact) => contact.contact_id}
              getOptionLabel={(contact) => contact.full_name}
              getOptionSub={(contact) => `${contact.customer_name || 'Unknown organisation'}${contact.email ? ` · ${contact.email}` : ''}`}
              onSelect={(row) => setSelected(row)}
              selectedIndicator={null}
            />
          </div>
          {selected && (
            <>
              <div className="link-contact-selected">
                Linking <strong>{selected.full_name}</strong> ({selected.customer_name}) to <strong>{customerName}</strong>
              </div>
              <div className="form-group">
                <label>Role at this organisation (optional)</label>
                <input
                  type="text"
                  value={linkRole}
                  onChange={(e) => setLinkRole(e.target.value)}
                  placeholder="e.g. Farm manager"
                />
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn-cancel" onClick={onClose}>Cancel</button>
          <button className="btn-save" onClick={submitLink} disabled={!selected || saving}>
            {saving ? 'Linking...' : 'Link Contact'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CustomerDetail;
